import React, {useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {connect} from 'react-redux';
import {Form, message, Radio, Checkbox, Input as AntdInput, Select as AntdSelect} from 'antd';
import LanguageSelect from 'components/LanguageSelect';
import {
    DatePicker,
    Departments,
    Input,
    InputNumber,
    OrganizationalUnits,
    RequestSelect,
    Select,
    Switch,
    TimeZoneInput
} from 'components/Form';
import EnableDisableButton from './EnableDisableButton';
import {PhoneNumberSelect, ComponentAccessCheck} from 'ui';
import apiClient from 'utils/apiClient';
import {UserFormDisplayInterface} from '../types';
import Spinner from 'components/Spinner';
import {config} from "config/config";
import moment from "moment";
import './styles.scss';
import {CustomValuesSelect} from "../../Form";
import coursesApiClient from "utils/coursesApiClient";
import useHandleError from "utils/useHandleError";

const formItemLayout = {
    labelCol: {span: 8},
    wrapperCol: {span: 6},
};

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    };
};

const UserFormDisplay: React.FC<UserFormDisplayInterface> = ({
                                                                 customerUser,
                                                                 disabledFields,
                                                                 customerParams,
                                                                 session,
                                                                 form,
                                                                 submitForm,
                                                                 userUuid,
                                                                 role,
                                                                 customerId,
                                                             }) => {

    const [accountStatuses, setAccountStatuses] = useState<any>({});
    const [isUserLoading, setIsUserLoading] = useState(true);
    const [isActiveLearningManagerRole, setIsActiveLearningManagerRole] = useState(false);
    const [isActiveElearningAccount, setIsActiveElearningAccount] = useState(false);
    const [sendEmail, setSendEmail] = useState(true);
    const [enableSmsSending, setEnableSmsSending] = useState(false);
    const [registerUserWithoutEmil, setRegisterUserWithoutEmil] = useState(false);
    const [customFields, setCustomFields] = useState<any>([]);
    const [handleError] = useHandleError();

    const loadCustomFields = async () => {
        try {
            const customFields = await coursesApiClient.request('/api/v1/users/custom-fields/list', [], 'GET');
            setCustomFields(customFields.fields);
        } catch (error) {
            handleError(error)
        } finally {
        }
    }

    const intl = useIntl();

    useEffect(() => {
        loadCustomFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadClassifiers = async () => {
            let url = '';

            if (role === 'students') {
                url = '/api/v1/enum/student-account-status-types';
            } else if (role === 'learning-managers') {
                url = '/api/v1/enum/customer-admin-account-status-types';
            } else {
                url = '/api/v1/enum/customer-admin-account-status-types';
            }

            try {
                let accountStatusesResponse = await apiClient.request(url, {}, 'GET');
                setAccountStatuses(accountStatusesResponse || {});
            } catch (err) {
                console.error(err);
            }
        };

        const customerDefaultSettings = async () => {
            try {
                let response = await apiClient.request(`/api/v1/customers/${customerId}/info`, {}, 'GET');
                form.setFieldsValue({
                    primaryLanguage: response.customer.defaultPrimaryLanguage,
                    timeZone: response.customer.defaultTimeZone,
                });
            } catch (err) {
                console.error(err);
            }
        };

        customerDefaultSettings();

        form.setFieldsValue({
            accountStatus: 'ACTIVE',
        });

        loadClassifiers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (customerUser.name) {
            setIsActiveLearningManagerRole(customerUser.isActiveLearningManagerRole === 1);
            setIsActiveElearningAccount(customerUser.isActiveElearningAccount === 1);

            if (customerUser.customFields && customerUser.customFields.length > 0) {
                Object.values(customerUser.customFields).map((field: any) => {
                    switch (field.field_type) {
                        case 'DATE':
                            form.setFieldsValue({
                                customFields: {
                                    [field.uuid]: moment(field.values)
                                }
                            })
                            break
                        default:
                            form.setFieldsValue({
                                customFields: {
                                    [field.uuid]: field.values
                                }
                            })
                            break
                    }
                });
            }

            let phoneNumber

            if (customerUser.phoneNumber) {
                phoneNumber = customerUser.phoneNumber.split(' ');
            }

            form.setFieldsValue({
                name: customerUser.name,
                surname: customerUser.surname,
                personalCode: customerUser.personalCode,
                email: customerUser.email,
                position: customerUser.position,
                organizationalUnitId: customerUser?.organizationalUnitId ?? null,
                departmentId: customerUser?.departmentId ?? null,
                managerReferenceId: customerUser.directManagerEmail,
                resourcePlanner: customerUser.resourcePlannerEmail,
                primaryLanguage: customerUser.primaryLanguage,
                commencementDate: customerUser.commencementDate ? moment(customerUser.commencementDate) : null,
                location: customerUser.location,
                timeZone: customerUser.timeZone,
                phishingAllowed: customerUser.phishingAllowed === 1,
                accountStatus: customerUser.accountStatus,
                isActiveElearningAccount: customerUser.isActiveElearningAccount === 1,
                isActiveSuperVision: customerUser.isActiveSuperVision === 1,
                absence: !!customerUser.absence,
                canManageLearningManagers: !!customerUser.canManageLearningManagers,
                canManageCustomers: !!customerUser.canManageCustomers,
                ignoreAccessPermissions: !!customerUser.ignoreAccessPermissions,
                canAddOwnContent: !!customerUser.canAddOwnContent,
                enableLector: !!customerUser.enableLector,
                phoneNumber: phoneNumber ? phoneNumber[1] : '',
                code: phoneNumber ? phoneNumber[0] : config.api.defaultCode,
                sendEmail: false,
                registerUserWithoutEmil: !!customerUser.smsSending,
                smsSending: !!customerUser.smsSending,
                canManageGeneralMessaging: customerUser.canManageGeneralMessaging,
                viewCustomerLogs: !!customerUser.viewCustomerLogs,
            });

            setRegisterUserWithoutEmil(!!customerUser.smsSending);
            setEnableSmsSending(!!customerUser.smsSending);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerUser]);

    useEffect(() => {
        if (role === 'learning-managers') {
            form.setFieldsValue({
                isActiveElearningAccount: true,
                canManageLearningManagers: false,
                isActiveSuperVision: false,
                canManageCustomers: false,
                ignoreAccessPermissions: false,
                viewCustomerLogs: false,
                phishingAllowed: false,
                absence: false,

                sendEmail: true
            });
        } else if (role === 'students') {
            form.setFieldsValue({
                isActiveElearningAccount: false,
                isActiveSuperVision: false,
                canManageLearningManagers: false,
                canManageCustomers: false,
                ignoreAccessPermissions: false,
                viewCustomerLogs: false,
                phishingAllowed: true,
                absence: false,
                sendEmail: true
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    return (
        <>
            <Form form={form} onFinish={submitForm} {...formItemLayout}>
                <Input disabled={disabledFields} name='name' label={intl.formatMessage({id: 'general.name'})}
                       validation={{required: true}}/>
                <Input disabled={disabledFields} name='surname'
                       label={intl.formatMessage({id: 'general.last_name'})} validation={{required: true}}/>
                <Input disabled={disabledFields} name='personalCode'
                       label={intl.formatMessage({id: 'general.personal_code'})} maxLength={20}/>
                <Input disabled={disabledFields} name='email' label={intl.formatMessage({id: 'general.email'})}
                        validation={{required: !registerUserWithoutEmil, type: 'email'}}/>
                <Input disabled={disabledFields} name='position'
                       label={intl.formatMessage({id: 'general.position'})}/>
                <PhoneNumberSelect
                    defaultCode={config.api.defaultCode}
                    wrapperClassName='phone-number-wrapper'
                    form={form} layout={formItemLayout}
                    name='phoneNumber'
                    label={intl.formatMessage({id: 'users.form.phone_number'})}
                />
                <OrganizationalUnits customerId={customerId}/>
                <Departments customerId={customerId}/>
                <RequestSelect
                    isForm={true}
                    label={intl.formatMessage({id: 'users.form.users_direct_manager'})}
                    selectClassName='request-select'
                    url={`/api/v1/edu/users/mass-search`}
                    param={[`customerId=${customerId}&key=reference_id&bothRoles=true`]}
                    column
                    name={'managerReferenceId'}
                    showSearch
                    allowClear
                />
                <ResourcePlannersSection
                    customerId={customerId || ''}
                    customerUser={customerUser}
                    formItemLayout={formItemLayout}
                    canEdit={session?.special_rights?.includes('edit_resource_planners') || session?.active_user_type === 'SUPER_ADMIN'}
                    form={form}
                />
                <LanguageSelect
                    name='primaryLanguage'
                    label={intl.formatMessage({id: 'general.language'})}
                    disabled={disabledFields}
                />
                <TimeZoneInput
                    name='timeZone'
                    label={intl.formatMessage({id: 'users.form.time_zone'})}
                    customLayout={formItemLayout}
                    customRules={[{required: true, message: intl.formatMessage({id: "validation.field_required"})}]}
                    disabled={disabledFields}
                />
                <DatePicker
                    label={intl.formatMessage({id: 'users.form.commencement_date'})}
                    name={'commencementDate'}
                    defaultValue={null}
                    format={config.defaultDateFormat}
                />
                <Switch onChange={() => setEnableSmsSending(!enableSmsSending)} isFormItem hasDefaultLayout
                        name='smsSending' label={intl.formatMessage({id: 'users.form.enable_sms_sending'})}/>
                <Switch className={!userUuid ? '' : ''} name='registerUserWithoutEmil' onChange={() => setRegisterUserWithoutEmil(!registerUserWithoutEmil)} isFormItem
                    hasDefaultLayout label={intl.formatMessage({ id: 'users.form.register_user_no_email' })} />
                {session.active_user_type !== 'SUPERVISION' &&
                    <>
                        {isActiveLearningManagerRole || role === 'learning-managers' ?
                            <Switch isFormItem hasDefaultLayout name='isActiveElearningAccount'
                                    label={intl.formatMessage({id: 'users.form.active_e_learning_account'})}/>
                            :
                            null
                        }
                        <Switch isFormItem hasDefaultLayout
                                name='isActiveSuperVision'
                                label={intl.formatMessage({id: 'users.form.is_active_super_vision'})}/>
                        {userUuid &&
                            <Switch isFormItem hasDefaultLayout name='absence'
                                    label={intl.formatMessage({id: 'users.form.long_absence'})}/>
                        }
                        <Form.Item {...formItemLayout} name='accountStatus'
                                   label={intl.formatMessage({id: 'users.form.account_status'})}>
                            <Radio.Group>
                                {Object.keys(accountStatuses).map(key =>
                                    <Radio disabled={disabledFields}
                                           className='radio-button'
                                           key={key}
                                           value={key}
                                    >
                                        {intl.formatMessage({id: `users.form.${key}`})}
                                    </Radio>
                                )}
                            </Radio.Group>
                        </Form.Item>
                        <ComponentAccessCheck permission={'view_phishing'}>
                            <Switch isFormItem hasDefaultLayout name='phishingAllowed'
                                    label={intl.formatMessage({id: 'users.form.phishing_allowed'})}
                                    disabled={disabledFields}/>
                        </ComponentAccessCheck>
                        {!userUuid &&
                            <Form.Item {...formItemLayout} name='sendEmail'
                                       label={intl.formatMessage({id: 'general.send_email'})}>
                                <Checkbox checked={sendEmail} onChange={() => {
                                    setSendEmail(!sendEmail);
                                    form.setFieldsValue({sendEmail: !sendEmail});
                                }}/>
                            </Form.Item>
                        }
                        {userUuid &&
                            <>
                                <EnableDisableButton
                                    userUuid={userUuid}
                                    from={form}
                                    isActiveLearningManagerRole={isActiveLearningManagerRole}
                                    setIsActiveLearningManagerRole={setIsActiveLearningManagerRole}
                                    isActiveElearningAccount={isActiveElearningAccount}
                                    setIsActiveElearningAccount={setIsActiveElearningAccount}
                                    customerId={customerId}
                                />
                                <Switch isFormItem hasDefaultLayout name="enableLector"
                                        label={intl.formatMessage({id: "organization.enable_lector"})}/>
                            </>
                        }
                        {
                            customFields.length > 0 ?
                                <>
                                    <div className='campaign-header-text'>
                                        <h1 className='text-18'>{intl.formatMessage({id: 'general.custom_fields'})}</h1>
                                    </div>
                                    {
                                        customFields.map((field: {
                                            uuid: string,
                                            field_type: 'TEXT' | 'INTEGER' | 'DATE' | 'SWITCH' | 'MULTIPLE_CHOICE' | 'HYPERLINK',
                                            title: string,
                                            multiple_choice_options: string[]
                                        }) => {

                                            switch (field.field_type) {
                                                case 'TEXT':
                                                    return (
                                                        <Input name={['customFields', field.uuid]}
                                                               label={field.title}></Input>
                                                    )
                                                case 'INTEGER':
                                                    return (
                                                        <InputNumber name={['customFields', field.uuid]}
                                                                     label={field.title}></InputNumber>
                                                    )
                                                case 'DATE':
                                                    return (
                                                        <DatePicker name={['customFields', field.uuid]}
                                                                    label={field.title}
                                                                    format={config.defaultDateFormat}></DatePicker>
                                                    )
                                                case 'SWITCH':
                                                    return (
                                                        <Switch name={['customFields', field.uuid]} isFormItem
                                                                label={field.title}></Switch>
                                                    )
                                                case 'MULTIPLE_CHOICE':
                                                    return (
                                                        <div className={'multiple-choice-fields'}>
                                                            <Form.Item label={field.title}
                                                                       name={['customFields', field.uuid]}>
                                                                <Checkbox.Group>
                                                                    {
                                                                        field.multiple_choice_options.map((el: string) => {
                                                                            return (
                                                                                <Checkbox value={el}>
                                                                                    {el}
                                                                                </Checkbox>
                                                                            )
                                                                        })
                                                                    }
                                                                </Checkbox.Group>
                                                            </Form.Item>
                                                        </div>
                                                    )
                                                case 'HYPERLINK':
                                                    return (
                                                        <Form.Item label={field.title}
                                                                   name={['customFields', field.uuid]}>
                                                            <AntdInput.Group compact>
                                                                <Form.Item
                                                                    className={'vertical-form-item'}
                                                                    label={intl.formatMessage({id: 'users.custom_filter_display_name'})}
                                                                    name={['customFields', field.uuid, 'displayName']}
                                                                >
                                                                    <AntdInput/>
                                                                </Form.Item>
                                                                <Form.Item
                                                                    className={'vertical-form-item'}
                                                                    label={intl.formatMessage({id: 'users.custom_filter_hyperlink'})}
                                                                    name={['customFields', field.uuid, 'hyperlink']}
                                                                >
                                                                    <AntdInput maxLength={1000}/>
                                                                </Form.Item>
                                                            </AntdInput.Group>
                                                        </Form.Item>
                                                    )
                                                default:
                                                    return;
                                            }
                                        })
                                    }
                                </>
                                :
                                null
                        }
                    </>
                }
                {session.active_user_type !== 'SUPERVISION' &&
                    <>
                        {isActiveLearningManagerRole || role === 'learning-managers' ?
                            <>
                                <hr className='form-group-separator'/>
                                <h1 className='form-group-header'>{intl.formatMessage({id: 'organization.special_rights'})}</h1>
                                <Switch isFormItem hasDefaultLayout name='ignoreAccessPermissions'
                                        label={intl.formatMessage({id: 'organization.ignore_access_permissions'})}/>
                                <Switch isFormItem hasDefaultLayout name='canManageLearningManagers'
                                        label={intl.formatMessage({id: 'users.form.add_remove_learning_manager'})}/>
                                <Switch isFormItem hasDefaultLayout name='canManageCustomers'
                                        label={intl.formatMessage({id: 'organizations.can_manage_customers'})}/>
                                {customerParams && customerParams.canAddOwnContent &&
                                    <Switch isFormItem hasDefaultLayout name='canAddOwnContent'
                                            label={intl.formatMessage({id: 'users.form.can_add_own_content'})}/>
                                }
                                <Switch isFormItem hasDefaultLayout name='canManageGeneralMessaging'
                                        label={intl.formatMessage({id: 'organizations.can_manage_general_messaging'})}/>
                                <Switch isFormItem hasDefaultLayout name='viewCustomerLogs'
                                        label={intl.formatMessage({id: 'system.audit.view_customer_logs'})}/>
                            </>
                            :
                            null
                        }
                    </>
                }
            </Form>
        </>
    );
};

interface ResourcePlannersSectionProps {
    customerId: string;
    customerUser: any;
    formItemLayout: any;
    form: any;
    canEdit: boolean;
}

const ResourcePlannersSection: React.FC<ResourcePlannersSectionProps> = ({
    customerId,
    customerUser,
    formItemLayout,
    form,
    canEdit,
}) => {
    const intl = useIntl();

    const structureRow = customerUser?.structurePlanners?.[0] ?? null;
    const allStructurePlanners: any[] = structureRow?.planners || [];
    const initialExcluded: number[] = customerUser?.excludedPlannerTypeIds || [];
    const initialDirectPlanners: any[] = customerUser?.directPlanners || [];

    const [excludedIds, setExcludedIds] = useState<number[]>(initialExcluded);
    const maxTotal = config.maxPlannerSlots;
    const [savedDirectIds, setSavedDirectIds] = useState<number[]>([]);
    const [newSlots, setNewSlots] = useState<(number | null)[]>([]);

    useEffect(() => {
        setExcludedIds(customerUser?.excludedPlannerTypeIds || []);
        const dp = customerUser?.directPlanners || [];
        setSavedDirectIds(dp.map((p: any) => p.userTypeId));
        setNewSlots([]);
    }, [customerUser]);

    const allDirectIds = [...savedDirectIds, ...newSlots.filter((v): v is number => v !== null)];

    useEffect(() => {
        form.setFieldsValue({
            excludedPlannerTypeIds: excludedIds,
            directPlannerTypeIds: allDirectIds,
        });
    }, [excludedIds, savedDirectIds, newSlots, form]);

    const visiblePlanners = allStructurePlanners.filter(
        (p: any) => !excludedIds.includes(p.userTypeId)
    );

    const excludedPlanners = allStructurePlanners.filter(
        (p: any) => excludedIds.includes(p.userTypeId)
    );

    const removePlanner = (userTypeId: number) => {
        setExcludedIds((prev) => [...prev, userTypeId]);
    };

    const restorePlanner = (userTypeId: number) => {
        setExcludedIds((prev) => prev.filter((id) => id !== userTypeId));
    };

    const structureLabel = structureRow
        ? (structureRow.unitTitle
            ? `${structureRow.departmentTitle} / ${structureRow.unitTitle}`
            : structureRow.departmentTitle)
        : null;

    // Derive display data from state
    const structureTypeIds = new Set(visiblePlanners.map((p: any) => p.userTypeId));
    const directTypeIds = new Set(allDirectIds);

    // Saved direct planners data (enriched from server), keyed by userTypeId
    const directPlannerMap = new Map<number, any>();
    (initialDirectPlanners || []).forEach((p: any) => directPlannerMap.set(p.userTypeId, p));

    // Saved direct-only = saved but NOT in active structure
    const savedDirectOnly = savedDirectIds.filter((id) => !structureTypeIds.has(id));

    // Occupied count = structure visible + saved direct-only + all new slots (filled or empty, each takes a seat)
    const occupiedCount = visiblePlanners.length + savedDirectOnly.length + newSlots.length;

    const tagStyle = (bg: string) => ({
        background: bg,
        color: '#fff',
        fontSize: 11,
        padding: '1px 6px',
        borderRadius: 3,
        lineHeight: '18px',
        pointerEvents: 'none' as const,
        marginLeft: 4,
    });

    const label = intl.formatMessage({ id: 'general.resource_planners' });

    return (
        <>
            <Form.Item label={label} {...formItemLayout}>
                <div>
                    {/* Active structure planners (some may also be direct) */}
                    {visiblePlanners.map((p: any) => {
                        const isAlsoDirect = directTypeIds.has(p.userTypeId);
                        return (
                            <div key={`struct_${p.id}`} style={{ marginBottom: 8, position: 'relative' }}>
                                <AntdSelect
                                    className="request-select"
                                    style={{ width: '100%' }}
                                    value={p.fullName || p.email || `ID: ${p.userId}`}
                                    open={false}
                                    allowClear={canEdit}
                                    onClear={() => canEdit && removePlanner(p.userTypeId)}
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: 32,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: 4,
                                    pointerEvents: 'none',
                                }}>
                                    <span style={tagStyle('#1890ff')}>
                                        {intl.formatMessage({ id: 'general.structure' })}
                                    </span>
                                    {isAlsoDirect && (
                                        <span style={tagStyle('#52c41a')}>
                                            {intl.formatMessage({ id: 'general.direct' })}
                                        </span>
                                    )}
                                </span>
                            </div>
                        );
                    })}

                    {/* Excluded structure planners */}
                    {excludedPlanners.map((p: any) => (
                        <div key={`excl_${p.id}`} style={{ marginBottom: 8, position: 'relative', opacity: 0.5 }}>
                            <AntdSelect
                                className="request-select"
                                style={{ width: '100%' }}
                                value={p.fullName || p.email || `ID: ${p.userId}`}
                                open={false}
                                disabled
                            />
                            {canEdit && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        cursor: 'pointer',
                                        color: '#1890ff',
                                        fontSize: 16,
                                        pointerEvents: 'auto',
                                    }}
                                    title={intl.formatMessage({ id: 'general.restore' })}
                                    onClick={() => restorePlanner(p.userTypeId)}
                                >
                                    ↩
                                </span>
                            )}
                        </div>
                    ))}

                    {/* Saved direct-only planners (not in structure) */}
                    {savedDirectOnly.map((typeId) => {
                        const p = directPlannerMap.get(typeId);
                        return (
                            <div key={`direct_${typeId}`} style={{ marginBottom: 8, position: 'relative' }}>
                                <AntdSelect
                                    className="request-select"
                                    style={{ width: '100%' }}
                                    value={p ? (p.fullName || p.email) : ''}
                                    open={false}
                                    allowClear={canEdit}
                                    onClear={() => canEdit && setSavedDirectIds((prev) => prev.filter((id) => id !== typeId))}
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: 32,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                }}>
                                    <span style={tagStyle('#52c41a')}>
                                        {intl.formatMessage({ id: 'general.direct' })}
                                    </span>
                                </span>
                            </div>
                        );
                    })}

                    {/* New direct planner selects (stay as searchable selects) */}
                    {canEdit && newSlots.map((slotValue, index) => (
                        <div key={`new_${index}`} style={{ marginBottom: 8 }}>
                            <RequestSelect
                                isForm={false}
                                selectClassName="request-select"
                                url={'/api/v1/edu/users/mass-search'}
                                param={[`customerId=${customerId}&key=type_id&bothRoles=false&onlyCustomer=true&supervision=true`]}
                                allowClear
                                onChange={(typeId: number | undefined) => {
                                    setNewSlots((prev) => {
                                        const next = [...prev];
                                        next[index] = typeId ?? null;
                                        return next;
                                    });
                                }}
                                column
                                name={`newDirectPlanner_${index}`}
                                showSearch
                            />
                        </div>
                    ))}

                    {canEdit && occupiedCount < maxTotal && (
                        <span
                            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                            onClick={() => setNewSlots((prev) => [...prev, null])}
                        >
                            + {intl.formatMessage({ id: 'general.add_planner' })}
                        </span>
                    )}
                </div>
            </Form.Item>
            <Form.Item name="excludedPlannerTypeIds" hidden>
                <AntdInput />
            </Form.Item>
            <Form.Item name="directPlannerTypeIds" hidden>
                <AntdInput />
            </Form.Item>
        </>
    );
};

export default connect(mapStateToProps)(UserFormDisplay);
